/**
 * Browser notification support for incoming in-app notifications.
 * Permission is requested only from an explicit user action (the notification bell).
 */

import { getStoredLanguage, translateArabicText } from '../lib/localization';

export interface BrowserNotificationInput {
  id?: string;
  title: string;
  message?: string;
}

export const isBrowserNotificationSupported = (): boolean =>
  typeof window !== 'undefined' && 'Notification' in window;

export async function requestBrowserNotificationPermission(): Promise<NotificationPermission | null> {
  if (!isBrowserNotificationSupported()) return null;
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Notification.permission;
  }

  try {
    return await Notification.requestPermission();
  } catch (error) {
    console.warn('Browser notification permission request failed:', error);
    return null;
  }
}

export function showBrowserNotification(notification: BrowserNotificationInput): boolean {
  if (!isBrowserNotificationSupported() || Notification.permission !== 'granted') return false;

  try {
    const localize = (value: string) => getStoredLanguage() === 'ar' ? translateArabicText(value) : value;
    const browserNotification = new Notification(localize(notification.title || 'New notification'), {
      body: notification.message ? localize(notification.message) : undefined,
      icon: '/favicon.ico',
      tag: notification.id ? `accounting-notification-${notification.id}` : undefined,
    });

    browserNotification.onclick = () => {
      window.focus();
      browserNotification.close();
    };

    return true;
  } catch (error) {
    console.warn('Browser notification could not be shown:', error);
    return false;
  }
}
